import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import type { UploadOutcropPicturesPageQuery } from '~/apollo/generated/v4/graphql';
import {
  LifecycleStatus,
  PictureInitialParentType,
} from '~/apollo/generated/v4/graphql';
import { NotFound } from '~/components/common/NotFound';
import { Panel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { OutcropThumbnailManager } from '~/components/upload/outcrop/outcrop-thumbnail-manager';
import { SOPictureManager } from '~/components/upload/supporting-object/so-picture-manager';
import { useShowUnpublished } from '~/components/upload/supporting-object/use-show-unpublished';
import { useRouteParam } from '~/hooks/routing';
import type { RefetchQueries } from '~/utils/graphql';
import { createRefetchQueries } from '~/utils/graphql';
import { countUnpublishedItems } from '~/utils/modules/supporting-object';

export const UPLOAD_OUTCROP_PICTURES_PAGE = graphql(`
  query UploadOutcropPicturesPage($id: ID!) {
    outcropGet(id: $id) {
      id
      name
      thumbnail {
        ...soManagerPictureParts
      }
      pictures {
        ...soManagerPictureParts
      }
    }
  }
`);

export default function UpdateOutcropPicturesRoute() {
  return (
    <ApolloProviderV4>
      <PageLoader />
    </ApolloProviderV4>
  );
}

export function PageLoader() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(UPLOAD_OUTCROP_PICTURES_PAGE, {
    variables: { id: outcropId },
  });

  const refetchQueries = createRefetchQueries([
    { query: UPLOAD_OUTCROP_PICTURES_PAGE, variables: { id: outcropId } },
  ]);

  const outcrop = data?.outcropGet;

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return <PageInner outcrop={outcrop} refetchQueries={refetchQueries} />;
}

type Outcrop = NonNullable<UploadOutcropPicturesPageQuery['outcropGet']>;

function PageInner({
  outcrop,
  refetchQueries,
}: {
  outcrop: Outcrop;
  refetchQueries: RefetchQueries;
}) {
  const { showUnpublished, PublishedToggle } = useShowUnpublished(
    countUnpublishedItems(outcrop.pictures),
  );

  const pictures = outcrop.pictures
    .filter(picture => {
      return (
        showUnpublished || picture.lifecycleStatus === LifecycleStatus.Published
      );
    })
    .sort(R.ascend(pic => pic.priority ?? Infinity));

  return (
    <div className="space-y-4">
      <Panel>
        <Panel.Heading>
          <Panel.Title>Thumbnail</Panel.Title>
        </Panel.Heading>
        <Panel.Body>
          <OutcropThumbnailManager
            outcropId={parseInt(outcrop.id)}
            outcropName={outcrop.name}
            thumbnail={outcrop.thumbnail}
            refetchQueries={refetchQueries}
          />
        </Panel.Body>
      </Panel>

      <PublishedToggle />

      <Panel>
        <Panel.Heading>
          <Panel.Title>Outcrop Pictures</Panel.Title>
        </Panel.Heading>
        <Panel.Body>
          <SOPictureManager
            parentType={PictureInitialParentType.Outcrop}
            parentId={parseInt(outcrop.id)}
            pictures={pictures}
            refetchQueries={refetchQueries}
          />
        </Panel.Body>
      </Panel>
    </div>
  );
}
